import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { FirebaseError } from 'firebase/app';
import {
  getMessaging,
  getToken,
  onMessage,
  Messaging,
  MessagePayload,
} from 'firebase/messaging';
import { firebaseApp } from '../firebase/firebase.config';
import { ApiNotificationsService } from './api/api-notifications.service';
import { Notification } from '../common/models/notification/notification';
import { PaginationOptions } from '@meplis/common';
import { CommonUrlBasedService } from './common.url-based.service';

export interface NotificationPayload {
  messageId?: string;
  from?: string;
  collapseKey?: string;
  data?: Record<string, string>;
  notification?: {
    title?: string;
    body?: string;
    [key: string]: string | undefined;
  };
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly messaging: Messaging;
  private readonly _messages$ = new BehaviorSubject<NotificationPayload | null>(
    null
  );
  private readonly _pushToken$ = new BehaviorSubject<string | null>(null);

  public readonly messages$: Observable<NotificationPayload | null> =
    this._messages$.asObservable();
  public readonly pushToken$: Observable<string | null> =
    this._pushToken$.asObservable();

  constructor(
    private _apiNotificationsService: ApiNotificationsService,
    private _commonService: CommonUrlBasedService
  ) {
    if (isPlatformBrowser(inject(PLATFORM_ID))) {
      this.messaging = getMessaging(firebaseApp);
    }
  }

  public get pushToken(): string | null {
    return this._pushToken$.value;
  }

  public init(): void {
    if (!this.messaging) {
      return;
    }
    onMessage(this.messaging, (payload: MessagePayload) => {
      const notification = this.toNotificationPayload(payload);
      this._messages$.next(notification);
    });
  }

  public async initializePushToken(
    vapidKey?: string,
    serviceWorkerScript = '/firebase-messaging-sw.js'
  ): Promise<string | null> {
    const registration = await this.registerServiceWorker(serviceWorkerScript);
    const token = await this.requestToken(vapidKey, registration ?? undefined);
    if (token) {
      this._pushToken$.next(token);
    }
    return token;
  }

  public async ensurePushToken(vapidKey?: string): Promise<string | null> {
    if (this.pushToken) {
      return this.pushToken;
    }

    return this.initializePushToken(vapidKey);
  }

  public getNotifications(
    paginationOptions: PaginationOptions,
    type: 'WEB' | 'ANDROID' | 'IOS' = 'WEB'
  ): Observable<Notification[]> {
    return from(this.ensurePushToken()).pipe(
      switchMap((token) => {
        if (!token) {
          return of([]);
        }

        return this._apiNotificationsService
          .registerNotificationToken(token, type)
          .pipe(
            switchMap(() =>
              this._apiNotificationsService.getNotifications(paginationOptions)
            )
          );
      })
    );
  }

  public async registerServiceWorker(
    scriptUrl = '/firebase-messaging-sw.js'
  ): Promise<ServiceWorkerRegistration | null> {
    if (!this._commonService.isPlatformBrowser) {
      console.warn(
        '[NotificationService] Service workers are unavailable because the platform is not a browser.'
      );
      return null;
    }

    if (!('serviceWorker' in navigator)) {
      console.warn(
        '[NotificationService] Service workers are unavailable in this browser.'
      );
      return null;
    }

    try {
      if (!this._commonService.isPlatformBrowser) {
        return null;
      }
      await navigator.serviceWorker.register(scriptUrl);
      return await navigator.serviceWorker.ready;
    } catch (error) {
      console.warn(
        '[NotificationService] Service worker registration failed.',
        error
      );
      return null;
    }
  }

  public async requestToken(
    vapidKey?: string,
    serviceWorkerRegistration?: ServiceWorkerRegistration
  ): Promise<string | null> {
    let registration = serviceWorkerRegistration;

    if (!this._commonService.isPlatformBrowser) {
      console.warn(
        '[NotificationService] Cannot request FCM token because the platform is not a browser.'
      );
      return null;
    }

    if (!registration) {
      console.warn(
        '[NotificationService] No service worker registration provided. Attempting to wait for an active registration.'
      );
      registration = await navigator.serviceWorker.ready;
    }

    if (!registration) {
      console.warn(
        '[NotificationService] Service worker registration is unavailable. Cannot request FCM token.'
      );
      return null;
    }

    try {
      const token = await getToken(this.messaging, {
        vapidKey,
        serviceWorkerRegistration: registration,
      });
      return token || null;
    } catch (error) {
      if (
        error instanceof FirebaseError &&
        error.code === 'messaging/failed-service-worker-registration'
      ) {
        console.warn(
          '[NotificationService] FCM token unavailable because the service worker registration failed.',
          error
        );
      } else {
        console.warn('[NotificationService] FCM token unavailable.', error);
      }
      return null;
    }
  }

  private toNotificationPayload(payload: MessagePayload): NotificationPayload {
    return {
      messageId: payload.messageId,
      from: payload.from,
      collapseKey: payload.collapseKey,
      data: payload.data ? { ...payload.data } : undefined,
      notification: payload.notification
        ? {
            title: payload.notification.title,
            body: payload.notification.body,
            ...payload.notification,
          }
        : undefined,
    };
  }
}
